import api from '@/lib/api';
import { useAuthStore, type ActingAs } from '@/store/authStore';

export interface ImpersonationTokenResponse {
  accessToken: string;
  actingAs: ActingAs;
  takeover: boolean;
  /** False for banned or deleted accounts: they can be viewed, never operated. */
  canTakeOver: boolean;
}

/**
 * Both calls reach the backend with the ADMIN's own token, but they get there
 * differently. `impersonate` runs before any borrowed session exists, so the
 * shared interceptor already attaches the right one. `takeControl` runs from
 * inside the borrowed session, where the active token is the target's: it has
 * to pin the admin's parked token by hand, or the request would be rejected
 * twice over — by `@PreAuthorize(ADMIN)`, because the borrowed token carries
 * the target's roles, and by the filter that 403s any non-GET request made
 * with a read-only support token.
 *
 * Reading the parked token here rather than taking it as a parameter is
 * deliberate: it keeps "takeover always travels on the admin's own token" in
 * one place, next to the endpoint that depends on it. A parameter would let any
 * future call site pass `accessToken` — the borrowed one — and silently
 * reintroduce the failure.
 *
 * Since neither endpoint ever sees a borrowed token, escalating from read-only
 * to control from inside a support session is impossible regardless of how the
 * UI behaves.
 */
export const adminImpersonationApi = {
  impersonate: async (
    targetPk: string,
    fromTournamentId?: string,
  ): Promise<ImpersonationTokenResponse> => {
    const response = await api.post('/admin/impersonation', {
      targetPk,
      fromTournamentId,
    });
    return response.data;
  },

  takeControl: async (targetPk: string): Promise<ImpersonationTokenResponse> => {
    const adminAccessToken =
      useAuthStore.getState().impersonation?.adminAccessToken;
    if (!adminAccessToken) {
      throw new Error('No hay una sesión de soporte activa.');
    }

    const response = await api.post(
      '/admin/impersonation/takeover',
      { targetPk },
      { headers: { Authorization: `Bearer ${adminAccessToken}` } },
    );
    return response.data;
  },
};
